// Where each roster is deep and where it is thin, position by position
// (TRADES.md "find a trade partner", B-388): the counts a manager reads to
// see who has what they lack, and the shortlist of teams the counts match.
//
// THE TIER at a position is the league's best players there by points a
// game, of those on a roster: as many as the league starts there, a team's
// slots for that position alone times the teams, and anybody level with the
// last of them. A flex is no position's own, so it is not counted: a team one
// over at running back may be starting him at the flex, which the counts
// show and a manager can weigh.
//
// DEEP is more of the tier than the team starts there; THIN is fewer. A
// PARTNER is a team deep where mine is thin, each with the positions that
// matched, so the manager sees the logic and can disagree with it; one also
// thin where mine is deep comes first, since each has what the other lacks
// (TRADES.md, "a surplus where this roster is thin and a need where it is
// deep"). Both ways alone left the list empty in a league as even as eight
// teams make it. A shortlist of teams, never a drafted offer, and never a
// sentence telling anybody to trade (CLAUDE.md, tools not instructions).
//
// ON THE WIRE (CLAUDE.md, the backend is a product): a second frontend
// reading the trades would otherwise count all of this again.

/** One rostered player, as the depth counts him. */
export interface DepthPlayer {
  playerId: string;
  position: string;
  /** Points a game this season; null before his first. */
  avg: number | null;
}

export interface DepthInput {
  /** The starting slots one position fills alone, in the lineup's order. */
  slots: readonly { position: string; count: number }[];
  /** Every team in the league's order, with its roster. */
  teams: readonly { teamId: string; players: readonly DepthPlayer[] }[];
  /** The reader's team; null for a reader with none, who gets no partners. */
  myTeamId: string | null;
}

export interface DepthPosition {
  position: string;
  /** The slots each team starts there. */
  slots: number;
  /** How many players the tier holds: the teams times the slots, and anybody level with the last. */
  tier: number;
}

export interface DepthTeam {
  teamId: string;
  /** How many of each position's tier the team holds, in `positions` order. */
  held: { position: string; count: number }[];
}

export interface DepthPartner {
  teamId: string;
  /** Where they hold more of the tier than they start, and I hold fewer than I start. */
  theyHaveMore: string[];
  /** Where I hold more than I start, and they hold fewer than they start; may be empty. */
  youHaveMore: string[];
}

export interface RosterDepth {
  positions: DepthPosition[];
  teams: DepthTeam[];
  /** Those that fit both ways first, then the most matched positions, then the league's order. */
  partners: DepthPartner[];
}

/**
 * Each team's share of each position's tier, and the teams that fit mine.
 * Null while no rostered player has a game this season: there is nothing
 * yet to rank anybody by.
 */
export function rosterDepth(input: DepthInput): RosterDepth | null {
  const everyone = input.teams.flatMap((t) => t.players.map((p) => ({ ...p, teamId: t.teamId })));
  if (!everyone.some((p) => p.avg !== null)) return null;
  // A position's slots summed, in the order the lineup first names it.
  const slotsAt = new Map<string, number>();
  for (const s of input.slots)
    if (s.count > 0) slotsAt.set(s.position, (slotsAt.get(s.position) ?? 0) + s.count);
  const positions: DepthPosition[] = [];
  const inTier = new Map<string, Set<string>>();
  for (const [position, slots] of slotsAt) {
    const ranked = everyone
      .filter((p) => p.position === position && p.avg !== null)
      .sort((a, b) => b.avg! - a.avg!);
    const want = slots * input.teams.length;
    const cut = ranked[Math.min(want, ranked.length) - 1]?.avg ?? null;
    const tier = cut === null ? [] : ranked.filter((p) => p.avg! >= cut);
    inTier.set(position, new Set(tier.map((p) => p.playerId)));
    positions.push({ position, slots, tier: tier.length });
  }
  const teams: DepthTeam[] = input.teams.map((t) => ({
    teamId: t.teamId,
    held: positions.map(({ position }) => ({
      position,
      count: t.players.filter((p) => inTier.get(position)!.has(p.playerId)).length,
    })),
  }));
  const countOf = (team: DepthTeam, position: string) =>
    team.held.find((h) => h.position === position)?.count ?? 0;
  const deep = (team: DepthTeam, p: DepthPosition) => countOf(team, p.position) > p.slots;
  const thin = (team: DepthTeam, p: DepthPosition) => countOf(team, p.position) < p.slots;
  const mine = teams.find((t) => t.teamId === input.myTeamId) ?? null;
  const partners: DepthPartner[] =
    mine === null
      ? []
      : teams
          .filter((t) => t !== mine)
          .map((t) => ({
            teamId: t.teamId,
            theyHaveMore: positions
              .filter((p) => deep(t, p) && thin(mine, p))
              .map((p) => p.position),
            youHaveMore: positions
              .filter((p) => deep(mine, p) && thin(t, p))
              .map((p) => p.position),
          }))
          .filter((p) => p.theyHaveMore.length > 0)
          .map((p, i) => ({ p, i }))
          .sort(
            (a, b) =>
              Number(b.p.youHaveMore.length > 0) - Number(a.p.youHaveMore.length > 0) ||
              b.p.theyHaveMore.length +
                b.p.youHaveMore.length -
                (a.p.theyHaveMore.length + a.p.youHaveMore.length) ||
              a.i - b.i,
          )
          .map(({ p }) => p);
  return { positions, teams, partners };
}
